import React from "react";
import type { Box } from "./frame";

/**
 * Anivaze v4 — PROCEDURAL VECTOR SETS (REAL-ANIMATION §6). Story beats play on
 * a real SET, not always the same meadow: a classroom, a lab bench, a city
 * street — each a pure-f(t) vector plate keyed off the beat's words, layered
 * into the multiplane background at its own parallax depth. Deterministic, $0.
 */

export type SetKind = "meadow" | "classroom" | "lab" | "street";

/** Keyword census per set. Street keeps only literal-place words — "stock
 *  market", "internet traffic", "the road to independence" must NOT urbanize. */
const SET_WORDS: [SetKind, RegExp][] = [
  ["classroom", /\b(school|classroom|teachers?|students?|grammar|spelling|exams?)\b/g],
  ["lab", /\b(science|scientific|scientists?|chemistry|chemicals?|experiments?|laborator(?:y|ies)|lab|molecules?|atoms?|physics|biology)\b/g],
  ["street", /\b(city|cities|streets?|towns?|urban|downtown|neighborhoods?)\b/g],
];

/** Pick the set from the words — the kind with the MOST keyword hits wins, and
 *  it takes at least 2 to leave the meadow (one stray word isn't a location). */
export function deriveSet(text: string): SetKind {
  const s = (text ?? "").toLowerCase();
  let best: SetKind = "meadow";
  let bestN = 1;
  for (const [kind, rx] of SET_WORDS) {
    const n = (s.match(rx) ?? []).length;
    if (n > bestN) { best = kind; bestN = n; }
  }
  return best;
}

/** Darken/lighten a #rrggbb hex by factor f (f<1 darkens). */
export function shade(hex: string, f: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `#${((ch(n >> 16 & 255) << 16) | (ch(n >> 8 & 255) << 8) | ch(n & 255)).toString(16).padStart(6, "0")}`;
}

interface SetProps { box: Box; gy: number; camX: number; t: number }

/** Indoor room shell: wall + floor cover the outdoor planes behind them.
 *  1.24w wide so the close-up camera pan never sees past the room's edge. */
function Room({ box, camX, gy, wall, floor }: SetProps & { wall: string; floor: string }) {
  return (
    <g transform={`translate(${camX * 0.2} 0)`}>
      <rect x={box.x - box.w * 0.12} y={box.y} width={box.w * 1.24} height={gy - box.y} fill={wall} />
      <rect x={box.x - box.w * 0.12} y={gy} width={box.w * 1.24} height={box.y + box.h - gy} fill={floor} />
      <line x1={box.x - box.w * 0.12} y1={gy} x2={box.x + box.w * 1.12} y2={gy} stroke={shade(floor, 0.8)} strokeWidth={box.h * 0.004} />
    </g>
  );
}

function ClassroomSet({ box, gy, camX, t }: SetProps) {
  const u = box.h;
  const bx = box.x + box.w * 0.1, bw = box.w * 0.46, by = box.y + u * 0.16, bh = u * 0.24;
  return (
    <g>
      <Room box={box} gy={gy} camX={camX} t={t} wall="#e8dcbc" floor="#c9a06a" />
      <g transform={`translate(${camX * 0.4} 0)`}>
        {/* blackboard with chalk squiggles (abstract — set dressing, not data) */}
        <rect x={bx - u * 0.012} y={by - u * 0.012} width={bw + u * 0.024} height={bh + u * 0.024} rx={u * 0.008} fill="#8a6b45" />
        <rect x={bx} y={by} width={bw} height={bh} fill="#2e4638" />
        <path d={`M ${bx + bw * 0.08} ${by + bh * 0.28} q ${bw * 0.1} ${-bh * 0.12} ${bw * 0.2} 0 t ${bw * 0.2} 0`} fill="none" stroke="#f4f0e2" strokeWidth={u * 0.006} opacity={0.75} strokeLinecap="round" />
        <path d={`M ${bx + bw * 0.08} ${by + bh * 0.55} q ${bw * 0.14} ${bh * 0.1} ${bw * 0.3} 0`} fill="none" stroke="#f4f0e2" strokeWidth={u * 0.006} opacity={0.6} strokeLinecap="round" />
        <circle cx={bx + bw * 0.78} cy={by + bh * 0.42} r={bh * 0.14} fill="none" stroke="#e8c46a" strokeWidth={u * 0.006} opacity={0.7} />
        {/* chalk rail */}
        <rect x={bx} y={by + bh + u * 0.012} width={bw} height={u * 0.014} fill="#8a6b45" />
        {/* window onto the sky, with a slow drifting cloud */}
        <rect x={box.x + box.w * 0.66} y={box.y + u * 0.14} width={box.w * 0.24} height={u * 0.2} fill="#cfe4ff" stroke="#a9885c" strokeWidth={u * 0.012} />
        {/* sinusoidal drift keeps the cloud inside the pane with NO wrap pop,
            however long the beat runs (pure f(t), no discontinuity) */}
        <ellipse cx={box.x + box.w * (0.78 + 0.06 * Math.sin(t * 0.05))} cy={box.y + u * 0.2} rx={box.w * 0.035} ry={u * 0.014} fill="#ffffff" opacity={0.8} />
        <line x1={box.x + box.w * 0.78} y1={box.y + u * 0.14} x2={box.x + box.w * 0.78} y2={box.y + u * 0.34} stroke="#a9885c" strokeWidth={u * 0.008} />
      </g>
      {/* desks — nearer plane */}
      <g transform={`translate(${camX * 0.7} 0)`}>
        {[0.16, 0.62].map((fx, i) => {
          const dx = box.x + box.w * fx, dw = box.w * 0.2, dy = gy - u * 0.001, dh = u * 0.055;
          return (
            <g key={i} fill="#a97c4f">
              <rect x={dx} y={dy - dh} width={dw} height={u * 0.018} rx={u * 0.006} />
              <rect x={dx + dw * 0.08} y={dy - dh + u * 0.018} width={u * 0.012} height={dh - u * 0.018} />
              <rect x={dx + dw * 0.84} y={dy - dh + u * 0.018} width={u * 0.012} height={dh - u * 0.018} />
            </g>
          );
        })}
      </g>
    </g>
  );
}

function LabSet({ box, gy, camX, t }: SetProps) {
  const u = box.h;
  const benchY = box.y + u * 0.60, benchH = u * 0.035;
  const bench = { x: box.x + box.w * 0.06, w: box.w * 0.55 };
  return (
    <g>
      <Room box={box} gy={gy} camX={camX} t={t} wall="#dce8ea" floor="#b9c4c9" />
      <g transform={`translate(${camX * 0.4} 0)`}>
        {/* periodic-table-ish poster (blank tiles — dressing, not data) */}
        <rect x={box.x + box.w * 0.7} y={box.y + u * 0.14} width={box.w * 0.2} height={u * 0.14} fill="#f0f4f5" stroke="#9fb2b8" strokeWidth={u * 0.004} />
        {[0, 1, 2, 3].map((row) => [0, 1, 2, 3, 4, 5].map((col) => (
          <rect key={`${row}-${col}`} x={box.x + box.w * (0.712 + col * 0.03)} y={box.y + u * (0.152 + row * 0.03)} width={box.w * 0.024} height={u * 0.022}
            fill={(row * 6 + col) % 5 === 0 ? "#8fd0b8" : (row + col) % 3 === 0 ? "#a9c4e0" : "#e4ecee"} />
        )))}
        {/* shelf with bottles */}
        <rect x={bench.x} y={box.y + u * 0.3} width={box.w * 0.4} height={u * 0.012} fill="#8a9aa4" />
        {[0.1, 0.2, 0.32].map((fx, i) => (
          <rect key={i} x={box.x + box.w * fx} y={box.y + u * 0.256} width={box.w * 0.035} height={u * 0.044} rx={u * 0.008}
            fill={["#5ec8a0", "#e8a25e", "#a98fd0"][i]} opacity={0.85} />
        ))}
      </g>
      {/* bench + glassware — nearer plane */}
      <g transform={`translate(${camX * 0.6} 0)`}>
        <rect x={bench.x} y={benchY} width={bench.w} height={benchH} rx={u * 0.008} fill="#7a8a94" />
        <rect x={bench.x + bench.w * 0.06} y={benchY + benchH} width={bench.w * 0.88} height={gy - benchY - benchH} fill="#5e6e78" />
        {/* conical flask with bubbling liquid (pure f(t) bubbles) */}
        <path d={`M ${box.x + box.w * 0.17} ${benchY - u * 0.075} L ${box.x + box.w * 0.17} ${benchY - u * 0.045} L ${box.x + box.w * 0.135} ${benchY} L ${box.x + box.w * 0.225} ${benchY} L ${box.x + box.w * 0.19} ${benchY - u * 0.045} L ${box.x + box.w * 0.19} ${benchY - u * 0.075} Z`}
          fill="#e9f2f4" stroke="#8a9aa4" strokeWidth={u * 0.004} opacity={0.9} />
        <path d={`M ${box.x + box.w * 0.152} ${benchY - u * 0.022} L ${box.x + box.w * 0.208} ${benchY - u * 0.022} L ${box.x + box.w * 0.216} ${benchY - u * 0.004} L ${box.x + box.w * 0.144} ${benchY - u * 0.004} Z`} fill="#5ec8a0" opacity={0.85} />
        {[0, 1, 2].map((i) => {
          const p = (t * (0.5 + i * 0.13) + i * 0.37) % 1;
          return <circle key={i} cx={box.x + box.w * (0.168 + i * 0.012)} cy={benchY - u * 0.008 - p * u * 0.05} r={u * (0.003 + 0.002 * (1 - p))} fill="#bfeadb" opacity={0.9 * (1 - p)} />;
        })}
        {/* beaker */}
        <rect x={box.x + box.w * 0.3} y={benchY - u * 0.055} width={box.w * 0.06} height={u * 0.055} fill="#e9f2f4" stroke="#8a9aa4" strokeWidth={u * 0.004} opacity={0.9} />
        <rect x={box.x + box.w * 0.303} y={benchY - u * 0.03} width={box.w * 0.054} height={u * 0.028} fill="#6aa9e8" opacity={0.8} />
        {/* test-tube rack */}
        <rect x={box.x + box.w * 0.44} y={benchY - u * 0.03} width={box.w * 0.1} height={u * 0.012} fill="#8a6b45" />
        {[0, 1, 2].map((i) => (
          <rect key={i} x={box.x + box.w * (0.452 + i * 0.028)} y={benchY - u * 0.052} width={box.w * 0.014} height={u * 0.05} rx={box.w * 0.007}
            fill={["#e86a6a", "#6aa9e8", "#e8c46a"][i]} opacity={0.75} />
        ))}
      </g>
    </g>
  );
}

function StreetSet({ box, gy, camX }: SetProps) {
  const u = box.h;
  const far = [
    { fx: 0.02, fw: 0.16, fh: 0.2 }, { fx: 0.2, fw: 0.12, fh: 0.3 }, { fx: 0.34, fw: 0.18, fh: 0.24 },
    { fx: 0.55, fw: 0.13, fh: 0.34 }, { fx: 0.7, fw: 0.2, fh: 0.22 },
  ];
  const near = [{ fx: 0.06, fw: 0.2, fh: 0.26 }, { fx: 0.58, fw: 0.24, fh: 0.3 }];
  return (
    <g>
      {/* far skyline */}
      <g transform={`translate(${camX * 0.35} 0)`}>
        {far.map((b, i) => (
          <rect key={i} x={box.x + box.w * b.fx} y={gy - u * b.fh} width={box.w * b.fw} height={u * b.fh} fill={i % 2 ? "#9aa7b8" : "#8b99ac"} />
        ))}
      </g>
      {/* near buildings with lit windows */}
      <g transform={`translate(${camX * 0.55} 0)`}>
        {near.map((b, i) => {
          const bx = box.x + box.w * b.fx, bw = box.w * b.fw, byy = gy - u * b.fh;
          return (
            <g key={i}>
              <rect x={bx} y={byy} width={bw} height={u * b.fh} fill={i ? "#6d7a8e" : "#7e8ba0"} />
              {[0, 1, 2].map((row) => [0, 1].map((col) => (
                <rect key={`${row}-${col}`} x={bx + bw * (0.18 + col * 0.42)} y={byy + u * (0.03 + row * 0.075)} width={bw * 0.22} height={u * 0.04}
                  fill={(i * 5 + row * 2 + col) % 3 === 0 ? "#ffd98a" : "#4a5568"} opacity={0.9} />
              )))}
            </g>
          );
        })}
      </g>
      {/* road + SIDEWALK over the ground plane (the cast stands on pavement,
          not the meadow) + a lamp */}
      <g transform={`translate(${camX * 0.85} 0)`}>
        <rect x={box.x - box.w * 0.12} y={gy} width={box.w * 1.24} height={u * 0.08} fill="#5a616b" />
        <rect x={box.x - box.w * 0.12} y={gy + u * 0.08} width={box.w * 1.24} height={box.y + box.h - gy - u * 0.08} fill="#a3a9b2" />
        <line x1={box.x - box.w * 0.12} y1={gy + u * 0.08} x2={box.x + box.w * 1.12} y2={gy + u * 0.08} stroke="#8a9099" strokeWidth={u * 0.006} />
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} x={box.x + box.w * (0.05 + i * 0.26)} y={gy + u * 0.037} width={box.w * 0.1} height={u * 0.007} rx={u * 0.003} fill="#e8e4d8" opacity={0.8} />
        ))}
        <rect x={box.x + box.w * 0.86} y={gy - u * 0.22} width={box.w * 0.012} height={u * 0.302} fill="#3f4854" />
        <rect x={box.x + box.w * 0.83} y={gy - u * 0.225} width={box.w * 0.07} height={u * 0.012} rx={u * 0.006} fill="#3f4854" />
        <circle cx={box.x + box.w * 0.845} cy={gy - u * 0.205} r={u * 0.014} fill="#ffd98a" />
        <circle cx={box.x + box.w * 0.845} cy={gy - u * 0.205} r={u * 0.035} fill="#ffd98a" opacity={0.18} />
      </g>
    </g>
  );
}

/** The set layer Background composes after its outdoor planes ("meadow" = none). */
export function SetLayer({ kind, box, gy, camX, t }: SetProps & { kind: SetKind }) {
  if (kind === "classroom") return <ClassroomSet box={box} gy={gy} camX={camX} t={t} />;
  if (kind === "lab") return <LabSet box={box} gy={gy} camX={camX} t={t} />;
  if (kind === "street") return <StreetSet box={box} gy={gy} camX={camX} t={t} />;
  return null;
}
